import { useContext, useEffect, useRef, useState } from 'react'

import Container from '@material-ui/core/Container'
import FormControl from '@material-ui/core/FormControl'
import Grid from '@material-ui/core/Grid'
import MenuItem from '@material-ui/core/MenuItem'
import Paper from '@material-ui/core/Paper'
import Button from '@material-ui/core/Button'
import Typography from '@material-ui/core/Typography'
import ReactSpeedometer from 'react-d3-speedometer'

// ICONS
import CheckCircleIcon from '@material-ui/icons/CheckCircle'
import ComputerIcon from '@material-ui/icons/Computer'
import GroupAddIcon from '@material-ui/icons/GroupAdd'
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty'
import ScheduleIcon from '@material-ui/icons/Schedule'
import TimerIcon from '@material-ui/icons/Timer'
import UpdateIcon from '@material-ui/icons/Update'

import { toast } from 'react-toastify'

import TableAttendantsStatus from '../../components/Dashboard/TableAttendantsStatus'
import DockerUpdatesBanner from '../../components/DockerUpdatesBanner'
import ProtectedRoute from '../../components/ProtectedRoute'

import { isEmpty } from 'lodash'
import moment from 'moment'
import { InputFieldText } from '../../components/InputFieldText'
import SelectInput from '../../components/SelectInput'
import { getTimezoneOffset } from '../../helpers/getTimezoneOffset.js'
import useAuth from '../../hooks/useAuth.js'
import useSettings from '../../hooks/useSettings'
import { i18n } from '../../translate/i18n'
import { TicketCountersChart } from './TicketCountersChart'

import { SocketContext } from '../../context/Socket/SocketContext.js'
import toastError from '../../errors/toastError'
import api from '../../services/api.js'
import { SmallPie } from './SmallPie.js'

import { useTheme } from '@material-ui/core/styles'
import { useStyles } from './style.js'

// const gitinfo = loadJSON('/gitinfo.json')

const isSubscriptionExpiredError = error => error?.response?.status === 402
const isForbiddenError = error => error?.response?.status === 403

const InfoCard = ({ title, value, icon }) => {
  const classes = useStyles()

  return (
    <Grid item xs={3}>
      <Paper className={classes.cardGray} elevation={0}>
        <div className={classes.cardData}>
          <Typography component="h3" variant="h6" paragraph>
            {title}
          </Typography>
          <Typography component="h1" variant="h4">
            {value}
          </Typography>
        </div>
        <div className={classes.cardIcon}>{icon}</div>
      </Paper>
    </Grid>
  )
}

const InfoRingCard = ({ title, value, graph }) => {
  const classes = useStyles()
  return (
    <Grid item xs={4}>
      <Paper className={classes.cardSolid} elevation={0}>
        <div className={classes.cardData}>
          <Typography
            component="h3"
            variant="h6"
            paragraph
            className={classes.cardTitle}
          >
            {title}
          </Typography>
          <Typography
            component="h1"
            variant="h4"
            className={classes.cardSubtitle}
          >
            {value}
          </Typography>
        </div>
        <div className={classes.cardRingGraph}>
          <div style={{ width: '100px' }}>{graph}</div>
        </div>
      </Paper>
    </Grid>
  )
}

const GlobalSummaryCard = ({ title, value }) => {
  const classes = useStyles()

  return (
    <Grid item xs={12} md={4}>
      <Paper className={classes.superSummaryCard} elevation={0}>
        <Typography className={classes.superSummaryTitle}>{title}</Typography>
        <Typography className={classes.superSummaryValue}>{value}</Typography>
      </Paper>
    </Grid>
  )
}

const formatBytes = bytes => {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 B'
  }

  const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB']
  const base = 1024
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(base)),
    units.length - 1
  )
  const value = bytes / base ** exponent

  return `${value.toFixed(exponent > 1 ? 1 : 0)} ${units[exponent]}`
}

const clampPercent = value => {
  const safeValue = Number(value)

  if (!Number.isFinite(safeValue)) {
    return 0
  }

  return Math.max(0, Math.min(100, safeValue))
}

const formatUptime = totalSeconds => {
  const safeSeconds = Number(totalSeconds)

  if (!Number.isFinite(safeSeconds) || safeSeconds <= 0) {
    return '0m'
  }

  const days = Math.floor(safeSeconds / 86400)
  const hours = Math.floor((safeSeconds % 86400) / 3600)
  const minutes = Math.floor((safeSeconds % 3600) / 60)

  if (days > 0) {
    return `${days}d ${hours}h ${minutes}m`
  }

  if (hours > 0) {
    return `${hours}h ${minutes}m`
  }

  return `${minutes}m`
}

const formatServerTime = isoDate => {
  if (!isoDate) {
    return '-'
  }

  const dateValue = new Date(isoDate)

  if (Number.isNaN(dateValue.getTime())) {
    return '-'
  }

  return dateValue.toLocaleString('pt-BR')
}

const getServerClockDisplay = (
  serverClock,
  fallbackIsoDate = null,
  _tick = 0
) => {
  if (!serverClock?.timestamp) {
    if (!fallbackIsoDate) {
      return '--:--:--'
    }

    return formatServerTime(fallbackIsoDate)
  }

  const estimatedNow = new Date(
    serverClock.timestamp + (performance.now() - serverClock.syncedAt)
  )

  const formatter = new Intl.DateTimeFormat('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: serverClock.timeZone
  })

  return `${formatter.format(estimatedNow)} (${serverClock.timeZone})`
}

const interpolateHexColor = (startHex, endHex, ratio) => {
  const safeRatio = Math.max(0, Math.min(1, ratio))

  const start = startHex.replace('#', '')
  const end = endHex.replace('#', '')

  const startR = parseInt(start.substring(0, 2), 16)
  const startG = parseInt(start.substring(2, 4), 16)
  const startB = parseInt(start.substring(4, 6), 16)

  const endR = parseInt(end.substring(0, 2), 16)
  const endG = parseInt(end.substring(2, 4), 16)
  const endB = parseInt(end.substring(4, 6), 16)

  const r = Math.round(startR + (endR - startR) * safeRatio)
  const g = Math.round(startG + (endG - startG) * safeRatio)
  const b = Math.round(startB + (endB - startB) * safeRatio)

  return `#${[r, g, b]
    .map(channel => channel.toString(16).padStart(2, '0'))
    .join('')}`
}

const buildSpeedometerGradient = segments => {
  const colors = []

  for (let index = 0; index < segments; index++) {
    const percent = (index / (segments - 1)) * 100

    if (percent <= 50) {
      colors.push(interpolateHexColor('#2ecc71', '#f39c12', percent / 50))
    } else if (percent <= 90) {
      colors.push('#f39c12')
    } else {
      colors.push(interpolateHexColor('#f39c12', '#e74c3c', (percent - 90) / 10))
    }
  }

  return colors
}

const SPEEDOMETER_SEGMENTS = 100
const SPEEDOMETER_GRADIENT_COLORS = buildSpeedometerGradient(SPEEDOMETER_SEGMENTS)

const HalfSpeedometerCard = ({ title, percentValue, subtitle }) => {
  const classes = useStyles()
  const speedometerValue = clampPercent(percentValue)

  return (
    <Grid item xs={12} md={4}>
      <Paper className={classes.systemMetricsCard} elevation={0}>
        <Typography className={classes.systemMetricsTitle}>{title}</Typography>
        <Typography className={classes.systemMetricsPercent}>
          {speedometerValue.toFixed(1)}%
        </Typography>
        <div className={classes.systemMetricsGauge}>
          <ReactSpeedometer
            minValue={0}
            maxValue={100}
            value={speedometerValue}
            segments={SPEEDOMETER_SEGMENTS}
            width={220}
            height={130}
            ringWidth={22}
            needleHeightRatio={0.7}
            segmentColors={SPEEDOMETER_GRADIENT_COLORS}
            currentValueText=""
            valueTextFontSize="0px"
            scaleFontSize={0}
            labelFontSize="0px"
            customTextRenderer={() => ''}
            needleTransition="easeQuadInOut"
            needleTransitionDuration={500}
          />
        </div>
        <Typography className={classes.systemMetricsSubtitle}>{subtitle}</Typography>
      </Paper>
    </Grid>
  )
}

const Dashboard = () => {
  const classes = useStyles()
  const [period, setPeriod] = useState(0)
  const [currentUser, setCurrentUser] = useState({})
  const [dateFrom, setDateFrom] = useState(
    moment('1', 'D').format('YYYY-MM-DD')
  )
  const [dateTo, setDateTo] = useState(moment().format('YYYY-MM-DD'))
  const { getCurrentUserInfo } = useAuth()

  // const [supportBoxOpen, setSupportBoxOpen] = useState(false)
  // const [registered, setRegistered] = useState(false)
  // const [proInstructionsOpen, setProInstructionsOpen] = useState(false)

  const [usersOnlineTotal, setUsersOnlineTotal] = useState(0)
  const [usersOfflineTotal, setUsersOfflineTotal] = useState(0)
  const [usersStatusChartData, setUsersStatusChartData] = useState([])
  const [pendingTotal, setPendingTotal] = useState(0)
  const [pendingChartData, setPendingChartData] = useState([])
  const [openedTotal, setOpenedTotal] = useState(0)
  const [openedChartData, setOpenedChartData] = useState([])

  const [ticketsData, setTicketsData] = useState({})
  const [usersData, setUsersData] = useState([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [globalSummary, setGlobalSummary] = useState({
    companiesTotal: 0,
    activeConnectionsTotal: 0,
    onlineUsersTotal: 0
  })
  const [systemMetrics, setSystemMetrics] = useState({
    cpuUsagePercent: 0,
    ramUsagePercent: 0,
    ramUsedBytes: 0,
    ramTotalBytes: 0,
    serverHostname: '-',
    operatingSystem: '-',
    uptimeSeconds: 0,
    serverTimeIso: null
  })
  const [diskMetrics, setDiskMetrics] = useState({
    usagePercent: 0,
    usedBytes: 0,
    totalBytes: 0
  })
  const [superConnections, setSuperConnections] = useState([])
  const [selectedSuperConnectionId, setSelectedSuperConnectionId] = useState('')
  const [restartingConnectionId, setRestartingConnectionId] = useState(null)
  const [serverClock, setServerClock] = useState(null)
  const [serverClockTick, setServerClockTick] = useState(0)
  const isMountedRef = useRef(true)

  const socketManager = useContext(SocketContext)
  const { getServerTime } = useSettings()

  const theme = useTheme()
  const companyId = localStorage.getItem('companyId')

  // async function showProInstructions() {
  //   if (gitinfo.commitHash) {
  //     setProInstructionsOpen(true)
  //     return
  //   }

  //   window.open('https://pro.ticke.tz', '_blank')
  // }

  useEffect(() => {
    const socket = socketManager.GetSocket(companyId)

    socket.on('userOnlineChange', updateStatus)
    socket.on('counter', updateStatus)

    return () => {
      socket.disconnect()
    }
  }, [socketManager])

  useEffect(() => {
    let isMounted = true
    getCurrentUserInfo().then(user => {
      if (!isMounted) return
      setCurrentUser(user)

      if (user?.super) {
        fetchGlobalSummary()
        fetchDiskMetrics()
        fetchSuperConnections()
      }
    })
    return () => { isMounted = false }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!currentUser?.super) {
      return undefined
    }

    const socket = socketManager.GetSocket(companyId)

    const onSystemMetrics = payload => {
      if (!payload) {
        return
      }

      setSystemMetrics({
        cpuUsagePercent: payload.cpu?.usagePercent || 0,
        ramUsagePercent: payload.ram?.usagePercent || 0,
        ramUsedBytes: payload.ram?.usedBytes || 0,
        ramTotalBytes: payload.ram?.totalBytes || 0,
        serverHostname: payload.server?.hostname || '-',
        operatingSystem: payload.server?.operatingSystem || '-',
        uptimeSeconds: payload.server?.uptimeSeconds || 0,
        serverTimeIso: payload.server?.timeIso || null
      })
    }

    socket.emit('joinSystemMetrics')
    socket.on('systemMetrics', onSystemMetrics)

    return () => {
      socket.emit('leaveSystemMetrics')
      socket.off('systemMetrics', onSystemMetrics)
    }
  }, [companyId, currentUser?.super, socketManager])

  useEffect(() => {
    if (!currentUser?.super) {
      return undefined
    }

    let isMounted = true

    const syncServerClock = async () => {
      try {
        const data = await getServerTime()

        if (!isMounted) {
          return
        }

        setServerClock({
          timestamp: Number(data?.timestamp),
          timeZone: data?.timeZone || 'UTC',
          syncedAt: performance.now()
        })
      } catch {
        if (isMounted) {
          setServerClock(null)
        }
      }
    }

    syncServerClock()

    const resyncInterval = setInterval(syncServerClock, 30000)
    const tickInterval = setInterval(() => {
      setServerClockTick(previous => previous + 1)
    }, 1000)

    return () => {
      isMounted = false
      clearInterval(resyncInterval)
      clearInterval(tickInterval)
    }
  }, [currentUser?.super, getServerTime])

  useEffect(() => {
    if (!currentUser?.super) {
      return undefined
    }

    const diskInterval = setInterval(() => {
      fetchDiskMetrics()
    }, 60000)

    return () => clearInterval(diskInterval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.super])

  useEffect(() => {
    fetchData()
  }, [period])

  async function handleChangePeriod(value) {
    setPeriod(value)
  }

  const serverClockDisplay = getServerClockDisplay(
    serverClock,
    systemMetrics.serverTimeIso,
    serverClockTick
  )

  async function updateStatus() {
    try {
      const { data } = await api.get('/dashboard/status')

      if (!data || !isMountedRef.current) return

      let usersOnlineTotal = 0
      let usersOfflineTotal = 0
      data.usersStatusSummary.forEach(item => {
        if (item.online) {
          usersOnlineTotal++
        } else {
          usersOfflineTotal++
        }
      })

      setUsersStatusChartData([
        {
          name: 'Online',
          value: usersOnlineTotal,
          color: theme.palette.teal_600.main
        },
        {
          name: 'Offline',
          value: usersOfflineTotal,
          color: theme.palette.red_500.main
        }
      ])

      setUsersOnlineTotal(usersOnlineTotal)
      setUsersOfflineTotal(usersOfflineTotal)

      let pendingTotal = 0
      let openedTotal = 0
      const pendingChartData = []
      const openedChartData = []
      data.ticketsStatusSummary.forEach(item => {
        if (item.status === 'pending') {
          pendingTotal += Number(item.count)
          pendingChartData.push({
            name: item.queue?.name || i18n.t('common.noqueue'),
            value: Number(item.count),
            color: item.queue?.color || '#888'
          })
          return
        }
        if (item.status === 'open') {
          openedTotal += Number(item.count)
          openedChartData.push({
            name: item.queue?.name || i18n.t('common.noqueue'),
            value: Number(item.count),
            color: item.queue?.color || '#888'
          })
        }
      })
      setPendingTotal(pendingTotal)
      setPendingChartData(pendingChartData)
      setOpenedTotal(openedTotal)
      setOpenedChartData(openedChartData)
    } catch (error) {
      if (!isSubscriptionExpiredError(error)) {
        toastError(error)
      }
    }
  }

  async function fetchData() {
    try {
      let params = { tz: getTimezoneOffset() }

      const days = Number(period)

      if (days) {
        params = {
          date_from: moment().subtract(days, 'days').format('YYYY-MM-DD'),
          date_to: moment().format('YYYY-MM-DD')
        }
      }

      if (!days && !isEmpty(dateFrom) && moment(dateFrom).isValid()) {
        params = {
          ...params,
          date_from: moment(dateFrom).format('YYYY-MM-DD')
        }
      }

      if (!days && !isEmpty(dateTo) && moment(dateTo).isValid()) {
        params = {
          ...params,
          date_to: moment(dateTo).format('YYYY-MM-DD')
        }
      }

      if (Object.keys(params).length === 0) {
        toast.error(i18n.t('dashboard.filter.invalid'))
        return
      }

      const [ticketsResult, usersResult] = await Promise.all([
        api.get('/dashboard/tickets', { params }),
        api.get('/dashboard/users', { params })
      ])

      if (!isMountedRef.current) return

      if (ticketsResult?.data) {
        setTicketsData(ticketsResult.data)
      }

      if (usersResult?.data) {
        setUsersData(usersResult.data)
      }
    } catch (error) {
      if (!isSubscriptionExpiredError(error) && !isForbiddenError(error)) {
        toastError(error)
      }
    } finally {
      if (isMountedRef.current) setLoadingUsers(false)
    }
  }

  async function fetchGlobalSummary() {
    try {
      const { data } = await api.get('/dashboard/global-summary')

      if (data && isMountedRef.current) {
        setGlobalSummary(data)
      }
    } catch (error) {
      if (!isSubscriptionExpiredError(error) && !isForbiddenError(error)) {
        toastError(error)
      }
    }
  }

  async function fetchDiskMetrics() {
    try {
      const { data } = await api.get('/dashboard/system/disk')

      if (data?.disk && isMountedRef.current) {
        setDiskMetrics({
          usagePercent: data.disk.usagePercent || 0,
          usedBytes: data.disk.usedBytes || 0,
          totalBytes: data.disk.totalBytes || 0
        })
      }
    } catch (error) {
      if (!isSubscriptionExpiredError(error) && !isForbiddenError(error)) {
        toastError(error)
      }
    }
  }

  async function fetchSuperConnections() {
    try {
      const { data } = await api.get('/dashboard/super/connections')

      const resolvedConnections = Array.isArray(data)
        ? data
        : data?.connections || []

      if (!isMountedRef.current) return

      setSuperConnections(resolvedConnections)

      if (!selectedSuperConnectionId && resolvedConnections.length > 0) {
        setSelectedSuperConnectionId(String(resolvedConnections[0].id))
      }
    } catch (error) {
      if (!isSubscriptionExpiredError(error) && !isForbiddenError(error)) {
        toastError(error)
      }
    }
  }

  async function handleRestartSelectedConnection() {
    if (!selectedSuperConnectionId) {
      toast.error('Selecione uma conexão para reiniciar')
      return
    }

    try {
      setRestartingConnectionId(selectedSuperConnectionId)
      await api.post(
        `/dashboard/super/connections/${selectedSuperConnectionId}/restart`
      )
      toast.success('Reinício da conexão solicitado com sucesso')
      fetchSuperConnections()
    } catch (error) {
      toastError(error)
    } finally {
      setRestartingConnectionId(null)
    }
  }

  useEffect(() => {
    updateStatus()
  }, [])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  function formatTime(minutes) {
    return moment().startOf('day').add(minutes, 'minutes').format('HH[h] mm[m]')
  }

  function renderFilters() {
    return (
      <>
        <Grid item xs={12} sm={6} md={3}>
          <FormControl className={classes.selectContainer}>
            {/* <InputLabel id="period-selector-label">
              Período
            </InputLabel> */}
            <SelectInput
              id="period-selector-label"
              name="period-selector-label"
              label="Período"
              value={period}
              onChange={e => handleChangePeriod(e.target.value)}
            >
              <MenuItem value={0}>{'Personalizado'}</MenuItem>
              <MenuItem value={3}>{'Últimos 3 dias'}</MenuItem>
              <MenuItem value={7}>{'Últimos 7 dias'}</MenuItem>
              <MenuItem value={15}>{'Últimos 14 dias'}</MenuItem>
              <MenuItem value={30}>{'Últimos 30 dias'}</MenuItem>
              <MenuItem value={90}>{'Últimos 90 dias'}</MenuItem>
            </SelectInput>
            {/* <Select
              labelId="period-selector-label"
              id="period-selector"
              value={period}
              onChange={e => handleChangePeriod(e.target.value)}
            >
              <MenuItem value={0}>{'Personalizado'}</MenuItem>
              <MenuItem value={3}>{'Últimos 3 dias'}</MenuItem>
              <MenuItem value={7}>{'Últimos 7 dias'}</MenuItem>
              <MenuItem value={15}>{'Últimos 14 dias'}</MenuItem>
              <MenuItem value={30}>{'Últimos 30 dias'}</MenuItem>
              <MenuItem value={90}>{'Últimos 90 dias'}</MenuItem>
            </Select> */}
          </FormControl>
        </Grid>
        {!period && (
          <>
            <Grid item xs={12} sm={6} md={3}>
              <InputFieldText
                label={'Data Inicial'}
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                onBlur={fetchData}
                className={classes.fullWidth}
              />
              {/* <TextField
                label={'Data Inicial'}
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                onBlur={fetchData}
                className={classes.fullWidth}
                InputLabelProps={{
                  shrink: true
                }}
              /> */}
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <InputFieldText
                label={'Data Final'}
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                onBlur={fetchData}
                className={classes.fullWidth}
              />
              {/* <TextField
                label={'Data Final'}
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                onBlur={fetchData}
                className={classes.fullWidth}
                InputLabelProps={{
                  shrink: true
                }}
              /> */}
            </Grid>
          </>
        )}
        <Grid item xs={12} sm={6} md={period ? 9 : 3} />
      </>
    )
  }

  return (
    <ProtectedRoute requiredRoles={['super', 'admin']} fallbackPath="/tickets">
      <div>
        <Container maxWidth="lg" className={classes.container}>
          {currentUser?.super && <DockerUpdatesBanner isSuper={currentUser?.super} />}
          {currentUser?.super && (
            <Grid container spacing={2}>
              <GlobalSummaryCard
                title={'Empresas'}
                value={globalSummary.companiesTotal}
              />
              <GlobalSummaryCard
                title={'Conexões Ativas'}
                value={globalSummary.activeConnectionsTotal}
              />
              <GlobalSummaryCard
                title={'Agentes Online'}
                value={globalSummary.onlineUsersTotal}
              />
            </Grid>
          )}

          <Grid container spacing={2}>
            {/* USUARIOS ONLINE */}
            <InfoRingCard
              // title={i18n.t('dashboard.usersOnline')}
              title={'Usuários Online'}
              value={`${usersOnlineTotal}/${usersOnlineTotal + usersOfflineTotal
                }`}
              graph={<SmallPie chartData={usersStatusChartData} />}
            />

            {/* ATENDIMENTOS PENDENTES */}
            <InfoRingCard
              // title={i18n.t('dashboard.ticketsWaiting')}
              title={'Atendimentos Pendentes'}
              value={pendingTotal}
              graph={<SmallPie chartData={pendingChartData} />}
            />

            {/* ATENDIMENTOS ABERTOS */}
            <InfoRingCard
              // title={i18n.t('dashboard.ticketsOpen')}
              title={'Atendimentos Abertos'}
              value={openedTotal}
              graph={<SmallPie chartData={openedChartData} />}
            />
          </Grid>

          {currentUser?.super && (
            <>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Paper className={classes.cardSolid} elevation={0}>
                    <div className={classes.superConnectionsContent}>
                      <Typography className={classes.superConnectionsTitle}>
                        Conexões de Todas as Empresas
                      </Typography>
                      <Typography className={classes.superConnectionsSubtitle}>
                        Selecione uma conexão para solicitar reinício da sessão.
                      </Typography>

                      <div className={classes.superConnectionsActions}>
                        <FormControl className={classes.superConnectionsSelect}>
                          <SelectInput
                            id="super-connections-selector"
                            name="super-connections-selector"
                            label="Conexão"
                            value={selectedSuperConnectionId}
                            onChange={e =>
                              setSelectedSuperConnectionId(String(e.target.value))
                            }
                          >
                            {superConnections.map(connection => (
                              <MenuItem
                                key={connection.id}
                                value={String(connection.id)}
                              >
                                {`${connection.companyName} - ${connection.name} (${connection.status || 'SEM STATUS'})`}
                              </MenuItem>
                            ))}
                          </SelectInput>
                        </FormControl>

                        <Button
                          variant="contained"
                          color="primary"
                          onClick={handleRestartSelectedConnection}
                          disabled={
                            !selectedSuperConnectionId ||
                            restartingConnectionId !== null
                          }
                        >
                          {restartingConnectionId
                            ? 'Reiniciando...'
                            : 'Reiniciar Conexão'}
                        </Button>
                      </div>
                    </div>
                  </Paper>
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <HalfSpeedometerCard
                  title={'CPU'}
                  percentValue={systemMetrics.cpuUsagePercent}
                  subtitle={'Média dos últimos 60 segundos'}
                />
                <HalfSpeedometerCard
                  title={'RAM'}
                  percentValue={systemMetrics.ramUsagePercent}
                  subtitle={`${formatBytes(systemMetrics.ramUsedBytes)} / ${formatBytes(
                    systemMetrics.ramTotalBytes
                  )}`}
                />
                <HalfSpeedometerCard
                  title={'Disco'}
                  percentValue={diskMetrics.usagePercent}
                  subtitle={`${formatBytes(diskMetrics.usedBytes)} / ${formatBytes(
                    diskMetrics.totalBytes
                  )}`}
                />
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Paper className={classes.cardSolid} elevation={0}>
                    <div className={classes.infoServerContent}>
                      <div className={classes.infoServerItem}>
                        <Typography className={classes.infoServerTitle}>
                          Servidor
                        </Typography>
                        <Typography className={classes.infoServerItemValue}>
                          <ComputerIcon fontSize="small" />
                          {systemMetrics.serverHostname}
                        </Typography>
                      </div>

                      <div className={classes.infoServerItem}>
                        <Typography className={classes.infoServerTitle}>
                          Sistema Operacional
                        </Typography>
                        <Typography className={classes.infoServerItemValue}>
                          <ComputerIcon fontSize="small" />
                          {systemMetrics.operatingSystem}
                        </Typography>
                      </div>

                      <div className={classes.infoServerItem}>
                        <Typography className={classes.infoServerTitle}>
                          Tempo Ligado
                        </Typography>
                        <Typography className={classes.infoServerItemValue}>
                          <UpdateIcon fontSize="small" />
                          {formatUptime(systemMetrics.uptimeSeconds)}
                        </Typography>
                      </div>

                      <div className={classes.infoServerItem}>
                        <Typography className={classes.infoServerTitle}>
                          Hora no Servidor
                        </Typography>
                        <Typography className={classes.infoServerItemValue}>
                          <ScheduleIcon fontSize="small" />
                          {serverClockDisplay}
                        </Typography>
                      </div>
                    </div>
                  </Paper>
                </Grid>
              </Grid>
            </>
          )}

          <Grid container spacing={2}>
            {/* ATENDIMENTOS CONCLUÍDOS */}
            <InfoCard
              // title={i18n.t('dashboard.ticketsDone')}
              title={'Atendimentos Concluídos'}
              value={ticketsData.ticketStatistics?.totalClosed || 0}
              icon={<CheckCircleIcon style={{ fontSize: 100 }} />}
            />
            {/* NOVOS CONTATOS */}
            <InfoCard
              // title={i18n.t('dashboard.newContacts')}
              title={'Novos Contatos'}
              value={ticketsData.ticketStatistics?.newContacts || 0}
              icon={<GroupAddIcon style={{ fontSize: 100 }} />}
            />

            {/* T.M. DE ATENDIMENTO */}
            <InfoCard
              // title={i18n.t('dashboard.avgServiceTime')}
              title={'T.M de Atendimento'}
              value={formatTime(ticketsData.ticketStatistics?.avgServiceTime)}
              icon={<TimerIcon style={{ fontSize: 100 }} />}
            />

            {/* T.M. DE ESPERA */}
            <InfoCard
              // title={i18n.t('dashboard.avgWaitTime')}
              title={'T.M de Espera'}
              value={formatTime(ticketsData.ticketStatistics?.avgWaitTime)}
              icon={<HourglassEmptyIcon style={{ fontSize: 100 }} />}
            />

            {/* FILTROS */}
            {renderFilters()}

            {/* DASHBOARD ATENDIMENTOS NO PERÍODO */}
            <Grid item xs={12}>
              <Paper className={classes.fixedHeightPaper}>
                <TicketCountersChart
                  ticketCounters={ticketsData.ticketCounters}
                  start={ticketsData.start}
                  end={ticketsData.end}
                />
              </Paper>
            </Grid>

            {/* USER REPORT */}
            <Grid item xs={12}>
              {usersData.userReport?.length ? (
                <TableAttendantsStatus
                  attendants={usersData.userReport}
                  loading={loadingUsers}
                />
              ) : null}
            </Grid>
          </Grid>
        </Container>
      </div>
    </ProtectedRoute>
  )
}

export default Dashboard
